import type { Slide, SlideElement } from '@/lib/documents';
import type { SuperStats } from '@/hooks/useSuperStats';

// ─────────────────────────────────────────────────────────────
// Présentation investisseurs Zifek.
// Construit un diaporama « riche » (logo + charte de couleurs) au format
// interne de l'app Documents, afin qu'il s'affiche et s'édite directement
// dans Documents Zifek — et qu'il s'exporte en .pptx depuis l'éditeur.
// ─────────────────────────────────────────────────────────────

export interface PresentationMeta {
  scopeLabel: string;
  startDate: string;
  endDate: string;
}

export interface BuiltPresentation {
  fileName: string;
  content: Record<string, unknown>;
}

export interface ZifekColorSwatch {
  label: string;
  value: string;
}

// Charte Zifek (source : branding global).
export const ZIFEK_COLORS = {
  navy: '#172C43',
  primary: '#155C9C',
  primaryDark: '#284875',
  turquoise: '#479CA7',
  green: '#356637',
  olive: '#718E50',
  terracotta: '#B36023',
  cream: '#F7F9F8',
  ivory: '#E3E2D9',
} as const;

export const ZIFEK_SWATCHES: ZifekColorSwatch[] = [
  { label: 'Bleu Zifek', value: ZIFEK_COLORS.primary },
  { label: 'Bleu nuit', value: ZIFEK_COLORS.primaryDark },
  { label: 'Turquoise', value: ZIFEK_COLORS.turquoise },
  { label: 'Terracotta', value: ZIFEK_COLORS.terracotta },
  { label: 'Vert', value: ZIFEK_COLORS.green },
  { label: 'Olive', value: ZIFEK_COLORS.olive },
  { label: 'Ivoire', value: ZIFEK_COLORS.ivory },
  { label: 'Crème', value: ZIFEK_COLORS.cream },
];

const NAVY = ZIFEK_COLORS.navy;
const PRIMARY = ZIFEK_COLORS.primary;
const TERRACOTTA = ZIFEK_COLORS.terracotta;
const CREAM = ZIFEK_COLORS.cream;
const INK = '#1F2937';
const MUTED = '#6B7280';
const WHITE = '#FFFFFF';
const TRACK = '#E4E7EB';

let seq = 0;
function uid(prefix: string): string {
  seq += 1;
  return `${prefix}-${seq}`;
}

function esc(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function num(value: number): string {
  return Math.round(value).toLocaleString('fr-FR');
}

function pct(value: number | null): string {
  if (value == null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function formatDateFr(value: string): string {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function shape(x: number, y: number, w: number, h: number, fill: string, radius = 0): SlideElement {
  return { id: uid('sh'), type: 'shape', x, y, w, h, fill, radius };
}

function text(
  x: number,
  y: number,
  w: number,
  h: number,
  html: string,
  align: 'left' | 'center' | 'right' = 'left',
  valign: 'top' | 'middle' | 'bottom' = 'top',
): SlideElement {
  return { id: uid('tx'), type: 'text', x, y, w, h, html, align, valign };
}

function richText(
  content: string,
  opts: { size?: number; color?: string; bold?: boolean; line?: number } = {},
): string {
  const { size = 14, color = INK, bold = false, line = 1.3 } = opts;
  return `<div style="font-size:${size}px;color:${color};font-weight:${bold ? 700 : 400};line-height:${line}">${content}</div>`;
}

/** Logo Zifek dessiné (marque « Z » + wordmark) — adapté au fond clair/sombre. */
function logoEls(theme: 'light' | 'dark', x: number, y: number, w = 4.6): SlideElement[] {
  const h = Math.round((w / 0.5625) * 10) / 10;
  const markFill = theme === 'dark' ? TERRACOTTA : PRIMARY;
  const nameColor = theme === 'dark' ? WHITE : NAVY;
  const zSize = Math.round(w * 4.4);
  const nameSize = Math.round(w * 3.9);
  return [
    shape(x, y, w, h, markFill, 10),
    text(
      x,
      y,
      w,
      h,
      `<div style="text-align:center;font-size:${zSize}px;font-weight:800;color:${WHITE};line-height:1">Z</div>`,
      'center',
      'middle',
    ),
    text(
      x + w + 1,
      y,
      18,
      h,
      `<div style="font-size:${nameSize}px;font-weight:800;letter-spacing:0.5px;color:${nameColor};line-height:1.1">Zifek</div>`,
      'left',
      'middle',
    ),
  ];
}

/** Rangée des couleurs de la charte (pastille + libellé). */
function swatchEls(x: number, y: number, step = 11.5, size = 5): SlideElement[] {
  const els: SlideElement[] = [];
  ZIFEK_SWATCHES.forEach((swatch, i) => {
    const cx = x + i * step;
    els.push(shape(cx, y, size, 9, swatch.value, 6));
    els.push(
      text(
        cx - (step - size) / 2,
        y + 9.6,
        step,
        8,
        `<div style="font-size:7px;color:${MUTED};text-align:center;line-height:1.15">${esc(swatch.label)}</div>`,
        'center',
        'top',
      ),
    );
  });
  return els;
}

/** Tuile KPI (fond crème, pastille chiffre + libellé). */
function tile(
  x: number,
  y: number,
  w: number,
  h: number,
  value: string,
  label: string,
  accent = false,
): SlideElement[] {
  const fill = accent ? PRIMARY : WHITE;
  const valueColor = accent ? WHITE : INK;
  const labelColor = accent ? '#DCE6F0' : MUTED;
  return [
    shape(x, y, w, h, fill, 10),
    text(x + 1.2, y + 1.6, w - 2.4, 8, richText(esc(value), { size: 24, color: valueColor, bold: true, line: 1.05 })),
    text(x + 1.2, y + h - 5.4, w - 2.4, 4.4, richText(esc(label), { size: 9, color: labelColor, line: 1.2 })),
  ];
}

/** Bandeau d'en-tête commun (fond navy + filet terracotta + logo + titre). */
function slideHeader(title: string, kicker: string): SlideElement[] {
  return [
    shape(0, 0, 100, 9, NAVY, 0),
    shape(0, 9, 100, 0.9, TERRACOTTA, 0),
    ...logoEls('dark', 3, 0.55, 4.4),
    text(21, 1.5, 75, 3, richText(kicker.toUpperCase(), { size: 9, color: '#9FC4E0', bold: true })),
    text(21, 4, 75, 4.2, richText(esc(title), { size: 17, color: WHITE, bold: true })),
  ];
}

/** Grille de 9 tuiles KPI à partir d'une liste [valeur, libellé, accent?]. */
function tileGrid(items: [string, string, boolean?][], startY = 12.5): SlideElement[] {
  const els: SlideElement[] = [];
  const cols = 3;
  const w = 29.25;
  const h = 22;
  const gapX = 3;
  const gapY = 3;
  items.forEach(([value, label, accent], i) => {
    const col = i % cols;
    const rowi = Math.floor(i / cols);
    els.push(...tile(3 + col * (w + gapX), startY + rowi * (h + gapY), w, h, value, label, !!accent));
  });
  return els;
}

export function buildZifekPresentation(stats: SuperStats, meta: PresentationMeta): BuiltPresentation {
  seq = 0;
  const slides: Slide[] = [];

  // ── 1. Couverture ─────────────────────────────────────────
  slides.push({
    id: uid('slide'),
    title: 'Couverture',
    body: '',
    bg: NAVY,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      shape(0, 96, 100, 1.7, TERRACOTTA, 0),
      ...logoEls('dark', 6, 8, 8),
      text(
        6,
        28,
        82,
        12,
        richText(
          'La plateforme SaaS tout-en-un : sites, boutique, marketplace, services et outils IA pour les créateurs et commerçants.',
          { size: 15, color: '#D6D3D1', line: 1.35 },
        ),
      ),
      text(6, 46, 70, 6, richText(`Dossier investisseurs — ${esc(meta.scopeLabel)}`, { size: 15, color: TERRACOTTA, bold: true })),
      text(
        6,
        53,
        70,
        8,
        richText(
          `Période analysée : ${formatDateFr(meta.startDate)} → ${formatDateFr(meta.endDate)}<br/>Généré le ${new Date().toLocaleString('fr-FR')}`,
          { size: 11, color: '#A8A29E', line: 1.4 },
        ),
      ),
      text(6, 68, 40, 4, richText('CHARTE GRAPHIQUE ZIFEK', { size: 8, color: '#9FC4E0', bold: true })),
      ...swatchEls(6, 73, 11.2, 4.8),
    ],
  });

  // ── 2. Synthèse exécutive ─────────────────────────────────
  slides.push({
    id: uid('slide'),
    title: 'Synthèse exécutive',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      ...slideHeader('Synthèse exécutive', 'Vue d’ensemble de la plateforme'),
      ...tileGrid([
        [num(stats.visitsAllTime), 'Visites depuis la création'],
        [num(stats.uniqueAllTime), 'Visiteurs uniques (total)'],
        [num(stats.visitsPeriod), `Visites (période) · ${pct(stats.deltas.visits)}`, true],
        [num(stats.uniquePeriod), `Visiteurs uniques (période) · ${pct(stats.deltas.unique)}`],
        [num(stats.sites), 'Sites de la plateforme'],
        [num(stats.siteUsers), 'Comptes utilisateurs'],
        [num(stats.marketplace.annoncesTotal), 'Annonces marketplace'],
        [num(stats.orders), `Commandes · ${pct(stats.deltas.orders)}`],
        [`${num(stats.revenue)} ${stats.currency}`, `CA commandes · ${pct(stats.deltas.revenue)}`, true],
      ]),
    ],
  });

  // ── 3. Traction ───────────────────────────────────────────
  slides.push({
    id: uid('slide'),
    title: 'Traction',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      ...slideHeader('Traction', 'Croissance et fidélité de l’audience'),
      ...tile(3, 15, 29.25, 30, num(stats.traction.views7d), `Vues 7 jours · ${pct(stats.traction.growth7d)}`, true),
      ...tile(35.25, 15, 29.25, 30, num(stats.traction.views30d), `Vues 30 jours · ${pct(stats.traction.growth30d)}`),
      ...tile(
        67.5,
        15,
        29.25,
        30,
        stats.traction.returningRate != null ? `${stats.traction.returningRate.toFixed(0)}%` : '—',
        `Taux de retour · ${num(stats.traction.returningVisitors)} / ${num(stats.traction.totalVisitors)} visiteurs`,
      ),
      text(
        3,
        50,
        94,
        14,
        richText(
          [
            'Mesures calculées sur les visites publiques des 60 derniers jours.',
            '• Croissance 7 j / 30 j : comparaison à la période précédente équivalente.',
            '• Taux de retour : visiteurs revenus au moins deux fois sur la plateforme.',
          ].join('<br/>'),
          { size: 12, color: '#374151', line: 1.5 },
        ),
      ),
    ],
  });

  // ── 4. Audience par type de page ──────────────────────────
  const typeRows: [string, number][] = [
    ['Accueil Zifek', stats.landingVisits],
    ['Sites de la plateforme', stats.siteVisits],
    ['Pages produits', stats.productVisits],
    ['Pages services', stats.serviceVisits],
  ];
  const typeMax = Math.max(1, ...typeRows.map(([, v]) => v));
  const audienceEls: SlideElement[] = [...slideHeader('Audience par type de page', 'Où se concentre le trafic')];
  typeRows.forEach(([label, value], i) => {
    const y = 15 + i * 16;
    audienceEls.push(text(4, y, 40, 4, richText(esc(label), { size: 12, color: '#374151' })));
    audienceEls.push(text(70, y, 26, 4, richText(num(value), { size: 12, color: INK, bold: true }), 'right'));
    audienceEls.push(shape(4, y + 5, 92, 2.6, TRACK, 4));
    audienceEls.push(shape(4, y + 5, Math.max(1, (92 * value) / typeMax), 2.6, i === 0 ? PRIMARY : TERRACOTTA, 4));
  });
  slides.push({
    id: uid('slide'),
    title: 'Audience par type de page',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: audienceEls,
  });

  // ── 5. Contenus & publications ────────────────────────────
  slides.push({
    id: uid('slide'),
    title: 'Contenus & publications',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      ...slideHeader('Contenus & publications', 'Richesse de l’écosystème'),
      ...tileGrid([
        [num(stats.marketplace.annoncesTotal), 'Annonces marketplace'],
        [num(stats.marketplace.annoncesProducts), 'Produits marketplace'],
        [num(stats.marketplace.annoncesServices), 'Services marketplace'],
        [num(stats.marketplace.annoncesPublished), 'Annonces publiées (période)'],
        [num(stats.marketplace.productItems), 'Produits boutique'],
        [num(stats.marketplace.digitalProducts), 'Produits numériques'],
        [num(stats.marketplace.servicesItems), 'Services des sites'],
        [num(stats.sites), 'Sites de la plateforme'],
        [num(stats.siteUsers), 'Comptes utilisateurs', true],
      ]),
    ],
  });

  // ── 6. Répartition par type de compte ─────────────────────
  const acctRows = (stats.accountTypes || []).filter((a) => a.value > 0);
  const acctTotal = acctRows.reduce((sum, a) => sum + a.value, 0);
  const acctEls: SlideElement[] = [...slideHeader('Répartition par type de compte', 'Boutique · Services · Mixte · Restauration')];
  if (acctRows.length === 0) {
    acctEls.push(text(4, 20, 90, 8, richText('Aucun site enregistré.', { size: 13, color: MUTED })));
  } else {
    const maxAcct = Math.max(1, ...acctRows.map((a) => a.value));
    acctRows.forEach((a, i) => {
      const y = 15 + i * 14;
      acctEls.push(text(4, y, 40, 4, richText(esc(a.label), { size: 12, color: '#374151' })));
      acctEls.push(text(70, y, 26, 4, richText(`${num(a.value)} · ${acctTotal > 0 ? ((a.value / acctTotal) * 100).toFixed(0) : 0}%`, { size: 12, color: INK, bold: true }), 'right'));
      acctEls.push(shape(4, y + 4.6, 92, 2.4, TRACK, 4));
      acctEls.push(shape(4, y + 4.6, Math.max(1, (92 * a.value) / maxAcct), 2.4, i % 2 === 0 ? PRIMARY : TERRACOTTA, 4));
    });
  }
  slides.push({
    id: uid('slide'),
    title: 'Répartition par type de compte',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: acctEls,
  });

  // ── 6b. Acquisition (nouveaux sites / comptes par mois) ───
  const acq = stats.acquisition || [];
  const acqEls: SlideElement[] = [
    ...slideHeader('Acquisition', 'Nouveaux sites & comptes par mois (12 mois)'),
    text(4, 13, 20, 4, richText('■ Sites', { size: 10, color: PRIMARY, bold: true })),
    text(20, 13, 34, 4, richText('■ Sites an dernier', { size: 10, color: MUTED, bold: true })),
    text(46, 13, 30, 4, richText('■ Comptes', { size: 10, color: TERRACOTTA, bold: true })),
  ];
  if (acq.length === 0) {
    acqEls.push(text(4, 22, 90, 8, richText("Aucune donnée d'acquisition disponible.", { size: 13, color: MUTED })));
  } else {
    const maxAcq = Math.max(1, ...acq.map((a) => Math.max(a.sites, a.accounts, a.sitesLastYear)));
    const stepX = 92 / acq.length;
    const baseLine = 82;
    const barW = stepX * 0.26;
    acq.forEach((a, i) => {
      const x = 4 + i * stepX;
      const h1 = (a.sites / maxAcq) * 52;
      const h2 = (a.sitesLastYear / maxAcq) * 52;
      const h3 = (a.accounts / maxAcq) * 52;
      acqEls.push(shape(x, baseLine - h1, barW, h1, PRIMARY, 3));
      acqEls.push(shape(x + barW, baseLine - h2, barW, h2, MUTED, 3));
      acqEls.push(shape(x + barW * 2, baseLine - h3, barW, h3, TERRACOTTA, 3));
      acqEls.push(
        text(
          x - stepX * 0.1,
          baseLine + 1.5,
          stepX,
          6,
          `<div style="font-size:8px;color:${MUTED};text-align:center;line-height:1">${esc(a.month)}</div>`,
          'center',
          'top',
        ),
      );
    });
  }
  slides.push({
    id: uid('slide'),
    title: 'Acquisition',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: acqEls,
  });

  // ── 7. Géographie ─────────────────────────────────────────
  const geoEls: SlideElement[] = [...slideHeader('Répartition géographique', 'D’où viennent les visiteurs')];
  if (stats.geoCountries.length === 0) {
    geoEls.push(text(4, 20, 90, 8, richText('Aucune donnée géographique disponible sur cette période.', { size: 13, color: MUTED })));
  } else {
    stats.geoCountries.slice(0, 8).forEach((country, i) => {
      const y = 15 + i * 10;
      geoEls.push(text(4, y, 60, 4, richText(`${esc(country.name)}${country.code ? ` (${esc(country.code)})` : ''}`, { size: 12, color: '#374151' })));
      geoEls.push(text(70, y, 26, 4, richText(num(country.value), { size: 12, color: INK, bold: true }), 'right'));
    });
  }
  slides.push({
    id: uid('slide'),
    title: 'Répartition géographique',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: geoEls,
  });

  // ── 8. Classements ────────────────────────────────────────
  const ranking = (title: string, items: { title: string; value: number }[]): string => {
    const lines = items.length === 0
      ? ['Aucune donnée']
      : items.slice(0, 5).map((it, i) => `${i + 1}. ${esc(it.title)} — ${num(it.value)}`);
    return richText(`<strong>${esc(title)}</strong>`, { size: 12, color: NAVY, bold: true }) + richText(lines.join('<br/>'), { size: 10, color: '#374151', line: 1.5 });
  };
  slides.push({
    id: uid('slide'),
    title: 'Classements',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      ...slideHeader('Classements', 'Sites, produits et services les plus visités'),
      text(3, 14, 30, 78, ranking('Sites les plus visités', stats.topSites)),
      text(36, 14, 30, 78, ranking('Produits les plus visités', stats.topProducts)),
      text(69, 14, 28, 78, ranking('Services les plus visités', stats.topServices)),
    ],
  });

  // ── 9. Charte graphique Zifek ─────────────────────────────
  slides.push({
    id: uid('slide'),
    title: 'Charte graphique Zifek',
    body: '',
    bg: CREAM,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      ...slideHeader('Charte graphique Zifek', 'Identité visuelle de la marque'),
      ...logoEls('light', 6, 16, 9),
      text(6, 40, 60, 4, richText('Palette officielle', { size: 11, color: NAVY, bold: true })),
      ...swatchEls(6, 47, 11.2, 5),
      text(
        6,
        66,
        88,
        20,
        richText(
          [
            'Bleu Zifek #155C9C — couleur principale (boutons, navigation, CTA).',
            'Bleu nuit #284875 · Turquoise #479CA7 — accents et éléments secondaires.',
            'Terracotta #B36023 · Vert #356637 · Olive #718E50 — teintes d’accent marocaines.',
            'Crème #F7F9F8 · Ivoire #E3E2D9 · Navy #172C43 — fonds et texte fort.',
          ].join('<br/>'),
          { size: 11, color: '#374151', line: 1.6 },
        ),
      ),
    ],
  });

  // ── 10. Vision & prochaines étapes ────────────────────────
  slides.push({
    id: uid('slide'),
    title: 'Vision & prochaines étapes',
    body: '',
    bg: NAVY,
    rich: true,
    canvasW: 960,
    canvasH: 540,
    elements: [
      shape(0, 30, 100, 0.9, TERRACOTTA, 0),
      ...logoEls('dark', 6, 6, 6),
      text(6, 32, 88, 6, richText('Vision & prochaines étapes', { size: 24, color: WHITE, bold: true })),
      text(
        6,
        42,
        88,
        42,
        richText(
          [
            '• Accélérer l’acquisition : campagnes, réseaux sociaux et programme de parrainage.',
            '• Développer la marketplace : plus de vendeurs, produits et services publiés.',
            '• Renforcer la rétention : fidélité, notifications et outils IA embarqués.',
            '• Internationaliser : multi-devises, multilingue et géolocalisation de l’audience.',
            '• Monétiser : abonnements, commission marketplace et services premium.',
          ].join('<br/>'),
          { size: 13, color: '#D6D3D1', line: 1.7 },
        ),
      ),
      text(6, 88, 88, 6, richText('Merci — zifek.com', { size: 13, color: TERRACOTTA, bold: true })),
    ],
  });

  const fileName = `Zifek — Présentation investisseurs (${meta.scopeLabel}) ${meta.startDate}_${meta.endDate}`;
  return { fileName, content: { slides } };
}